import { noop, map as mapSpecificKey, isEqual } from 'lodash'
import { useState, useRef, useEffect } from 'react'
import MultiSelectSearchDropDown from '../../select-dropdown/MultiSelectSearchDropDown'
import { StringFilterInputPropsType } from '../../../utils/types/tableFilterTypes'
import {
  DropdownType,
  filterOperatorValues,
  formatValueWithPattern,
  tableFilterType,
} from '../../../utils'

export const StringFilterInput = <T,>({
  columnData,
  handleFilterChange = noop,
  filterObject,
  dataQaId,
  filterOperator,
  columnHeaderRef,
  innerInputRef,
  tabIndex,
}: StringFilterInputPropsType<T>) => {
  const search_key =
    columnData?.filterData?.convertToUppercase === false
      ? (columnData?.accessor as string)
      : (columnData?.accessor as string)?.toUpperCase()
  const [dropdownOptions, setDropdownOptions] = useState<
    DropdownType<string>[]
  >([])
  const [selectedOptions, setSelectedOptions] = useState<
    DropdownType<T>[] | undefined
  >(undefined)
  const [dropdownDisabled, setDropdownDisabled] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [showClearFilter, setShowClearFilter] = useState<boolean>(false)
  const [renderKey, setRenderKey] = useState<boolean>(false)

  const getFilterOperator = () => {
    if (filterOperator?.value !== filterOperatorValues.CONTAINS)
      return filterOperator?.value ?? ''
    if (columnData.filterData?.dataType === tableFilterType.STRING_DYNAMIC) {
      return filterOperatorValues.LIKE
    } else {
      return filterOperatorValues.CONTAINS
    }
  }

  const onFocusOut = (e: DropdownType<T>[]) => {
    let filterOperatorType =
      filterObject?.search_criteria?.[search_key]?.operator
    if (
      columnData?.filterData?.dataType === tableFilterType?.STRING_DYNAMIC &&
      filterOperatorType === filterOperatorValues?.LIKE
    ) {
      filterOperatorType = filterOperatorValues?.CONTAINS
    }
    if (
      !isEqual(
        mapSpecificKey(e, 'value'),
        filterObject?.search_criteria?.[search_key]?.search_value
      ) ||
      filterOperatorType !== filterOperator?.value
    ) {
      handleFilterChange({
        search_key: search_key,
        operator: getFilterOperator(),
        search_value: mapSpecificKey(e, 'value'),
      })
    }
    setDropdownOptions([])
  }

  const getDropdownOptions = async (searchTerm: string) => {
    if (!columnData.filterData?.onKeyPress) {
      setDropdownOptions([])
      return
    }
    const searchCriteria = {
      search_key: search_key,
      operator: filterOperatorValues.LIKE,
      search_value: [searchTerm],
    }
    const data = await columnData.filterData.onKeyPress(searchCriteria)
    setDropdownOptions(
      data.map((val: string) => ({
        label: columnData.filterData?.format
          ? formatValueWithPattern(val, columnData.filterData.format)
          : val,
        value: val,
      }))
    )
  }

  const getDropdownOptionsOnFocus = async (
    searchTerm: string,
    page: number,
    dataSize: number
  ): Promise<{ data: string[]; size: number }> => {
    if (!columnData.filterData?.onFocus) {
      setDropdownOptions([])
      return { data: [], size: 0 }
    }
    const searchCriteria = {
      search_key: search_key,
      operator: filterOperatorValues.LIKE,
      search_value: [searchTerm],
    }
    const { data, size } = await columnData.filterData.onFocus(
      searchCriteria,
      page,
      dataSize
    )
    setDropdownOptions((prev) => {
      const newOptions = data.map((val: string) => ({
        label: columnData.filterData?.format
          ? formatValueWithPattern(val, columnData.filterData.format)
          : val,
        value: val,
      }))
      const merged = [...prev, ...newOptions]
      const unique = Array.from(
        new Map(merged.map((item) => [item.value, item])).values()
      )

      return unique
    })
    return { data, size }
  }

  useEffect(() => {
    if (filterObject?.search_criteria?.[search_key]) {
      setSelectedOptions(
        filterObject.search_criteria[search_key].search_value
          .filter((item) => item)
          .map((item) => ({
            label: item,
            value: item,
          }))
      )
    } else {
      setSelectedOptions([])
    }
  }, [filterObject, search_key])

  useEffect(() => {
    if (
      filterOperator?.value === filterOperatorValues.IS_EMPTY_NULL ||
      filterOperator?.value === filterOperatorValues.IS_NOT_EMPTY_NOT_NULL
    ) {
      setDropdownDisabled(true)
      handleFilterChange({
        search_key: search_key,
        operator: filterOperator.value ?? '',
        search_value: [''],
      })
      setDropdownOptions([])
      setSelectedOptions([])
      setShowClearFilter(true)
      setRenderKey((prev) => !prev)
    } else {
      setShowClearFilter(false)
      setDropdownDisabled(false)
    }
  }, [filterOperator])

  useEffect(() => {
    innerInputRef.current = inputRef.current
  }, [filterOperator])

  return (
    <div className="flex-1" key={renderKey.toString()}>
      <MultiSelectSearchDropDown
        options={dropdownOptions}
        name={columnData.accessor as string}
        label=""
        required={false}
        onSearch={getDropdownOptions}
        onFocusOut={onFocusOut}
        defaultSelected={selectedOptions}
        inpref={inputRef}
        dataQaId={`${dataQaId}_dropdown`}
        disabled={dropdownDisabled || columnData?.isFilterDisabled}
        format={columnData.filterData?.format}
        showCrossIcon={showClearFilter}
        excludedParentRef={columnHeaderRef}
        isFilterApplied={
          (filterObject?.search_criteria?.[search_key]?.search_value?.length ??
            0) > 0 ||
          filterOperator?.value === filterOperatorValues.IS_EMPTY_NULL ||
          filterOperator?.value === filterOperatorValues.IS_NOT_EMPTY_NOT_NULL
        }
        isFromFilter={true}
        tabIndex={
          dropdownDisabled || columnData?.isFilterDisabled ? -1 : tabIndex
        }
        isFilterDisabled={columnData?.isFilterDisabled}
        onFocusClick={(searchTerm, page, size) =>
          getDropdownOptionsOnFocus(searchTerm, page, size)
        }
        size={10}
      />
    </div>
  )
}
